/**
 * LA CUBIERTA PARA KDP (26-sep-2026), el flujo de Cowork en el Studio. Rais: «me iba a otra IA, me
 * generaba la portada, escogía una y luego Cowork me hacía el wrap para publicarlo».
 *
 * Aquí: subes la portada que elegiste, eliges la contraportada (las tres versiones de tu encargo,
 * editables) y revisas la bio y el lomo. El motor monta portada, lomo y contraportada con el script
 * de la skill cover —medidas exactas de KDP desde las páginas REALES de tu maquetación, 600 ppp,
 * texto nítido y el hueco del código de barras libre— y te enseña la prueba con sus guías.
 */
import { useEffect, useState } from "react";
import { datosCubierta, imagenDelLibro, montarCubierta, mostrarEnCarpeta, subirPortada } from "../api";
import type { DatosCubierta, PortadaSubida, ResultadoCubierta, Trabajo } from "../api";
import { nombreDeRuta } from "../logica";
import { ListaDePasos } from "./ListaDePasos";

const mm = (x: number) => String(x).replace(".", ",");

export default function Cubierta({ carpeta, doc, trabajo, pulso, alLanzar }: {
  carpeta: string; doc: string | null; trabajo: Trabajo | null; pulso?: number; alLanzar: () => void;
}) {
  const [datos, setDatos] = useState<DatosCubierta | null | undefined>(undefined);
  const [portada, setPortada] = useState<PortadaSubida | null>(null);
  const [vista, setVista] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [contra, setContra] = useState("");
  const [titular, setTitular] = useState("");
  const [bio, setBio] = useState("");
  const [titulo, setTitulo] = useState("");
  const [autor, setAutor] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [prueba, setPrueba] = useState<string | null>(null);
  const enCurso = trabajo?.estado === "en_curso";
  const hecho = (trabajo?.estado === "hecho" ? trabajo.resultado as unknown as ResultadoCubierta : null) ?? datos?.hecho ?? null;

  useEffect(() => {
    if (!carpeta) return;
    datosCubierta(carpeta, doc).then((d) => {
      setDatos(d);
      if (!d) return;
      setTitulo((t) => t || d.titulo); setAutor((a) => a || d.autor); setBio((b) => b || d.bio || "");
      const v = d.contraportada?.recomendada ?? null;
      if (v && !contra) { setVersion(v); setContra(d.contraportada!.versiones[v]); }
    }).catch(() => setDatos(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carpeta, doc, pulso, trabajo?.estado]);
  useEffect(() => {
    if (!hecho?.prueba) { setPrueba(null); return; }
    let url: string | null = null;
    imagenDelLibro(carpeta, hecho.prueba).then((u) => { url = u; setPrueba(u); });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [carpeta, hecho?.prueba]);

  if (datos === null) {
    return (
      <section className="carta">
        <h2>La cubierta para KDP</h2>
        <div className="aviso">Tu motor aún no monta cubiertas: instala la última versión del Studio (en Ajustes → Descargas).</div>
      </section>
    );
  }

  async function subir(f: File | undefined) {
    if (!f) return;
    setFallo(null); setOcupado(true);
    try {
      const p = await subirPortada(carpeta, f, datos?.maquetacion?.tamano ?? "6x9");
      setPortada(p);
      if (vista) URL.revokeObjectURL(vista);
      setVista(URL.createObjectURL(f));
    } catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }
  async function montar() {
    if (!portada) return;
    setFallo(null); setOcupado(true);
    try { await montarCubierta({ dir: carpeta, doc, portada: portada.ruta, contraportada: contra, bio, titulo, autor, titular }); alLanzar(); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  const m = datos?.maquetacion;
  const versiones = datos?.contraportada?.versiones ?? {};
  const listo = !!portada && !!m && contra.trim() && titulo.trim() && autor.trim();
  return (
    <section className="carta">
      <h2>La cubierta para KDP<span className="sub">portada, lomo y contraportada, a medidas exactas</span></h2>
      <p className="vacio">
        Sube la portada que has elegido (la que te ha hecho otra IA con los prompts de «Portada: tres conceptos»). Yo monto
        la cubierta entera para la tapa blanda: el lomo con el grosor real de tu libro, la contraportada y la bio en texto
        nítido a 600 ppp, y el hueco del código de barras libre para Amazon.
      </p>
      {!m && <div className="aviso">Maqueta antes el libro (arriba, «Maquetar mi libro»): el lomo sale del número de páginas real del PDF de imprenta.</div>}
      {m && <p className="vacio">Tu libro maquetado: <b>{m.paginas} páginas</b>, {m.tamano.replace("x", " × ")}, papel {m.papel}{m.lomo ? `, lomo de ${mm(m.lomo.mm)} mm` : ""}.</p>}
      {!enCurso && (
        <div className="formulario cubierta">
          <label className="campo"><span>1 · La portada que has elegido <i>(PNG, JPG o WebP)</i></span>
            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={ocupado} onChange={(e) => subir(e.target.files?.[0])} />
          </label>
          {portada && (
            <div className="cubierta-portada">
              {vista && <img src={vista} alt="La portada elegida" />}
              <p className={portada.ppp < 300 ? "aviso" : "vacio"}>
                {portada.ancho} × {portada.alto} px · imprime a unos <b>{portada.ppp} ppp</b>.
                {portada.ppp < 300 ? ` Para imprenta hacen falta 300: saldría borrosa. Pídele a la IA la imagen a ${portada.anchoPara300} px de ancho o más (o escálala), y vuelve a subirla. Puedo montarla igual para que veas cómo queda.` : " Bien para imprenta."}
              </p>
            </div>
          )}
          <label className="campo"><span>2 · La contraportada {Object.keys(versiones).length ? <i>(de tu encargo «Contraportada y biografía»; puedes retocarla)</i> : <i>(pide antes «Contraportada y biografía», o escríbela aquí)</i>}</span>
            {Object.keys(versiones).length > 0 && (
              <div className="chips">
                {Object.keys(versiones).map((v) => (
                  <button key={v} className={`btn texto${version === v ? " on" : ""}`} onClick={() => { setVersion(v); setContra(versiones[v]); }}>
                    Versión {v}{datos?.contraportada?.recomendada === v ? " (la recomendada)" : ""}
                  </button>
                ))}
              </div>
            )}
            <textarea className="escribir" rows={8} value={contra} onChange={(e) => setContra(e.target.value)} />
          </label>
          <label className="campo"><span>Titular de la contraportada <i>(opcional: una frase arriba, en grande)</i></span>
            <input className="escribir" value={titular} onChange={(e) => setTitular(e.target.value)} />
          </label>
          <label className="campo"><span>La bio <i>{datos?.bioDe ? `(de ${datos.bioDe})` : "(la de tu perfil de autor; si no lo tienes, escríbela aquí)"}</i></span>
            <textarea className="escribir" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
          </label>
          <label className="campo"><span>3 · El lomo: título</span>
            <input className="escribir" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </label>
          <label className="campo"><span>El lomo: autor</span>
            <input className="escribir" value={autor} onChange={(e) => setAutor(e.target.value)} />
          </label>
          <div className="pie-form">
            <p className="vacio">Sin IA y sin coste · tarda unos segundos</p>
            <button className="btn" disabled={!listo || ocupado} onClick={montar}>{hecho ? "Montar otra vez" : "Montar la cubierta"}</button>
          </div>
        </div>
      )}
      {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
      {trabajo?.estado === "fallido" && <p className="vacio" style={{ color: "var(--acc)" }}>No se ha podido montar: {trabajo.error}</p>}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      {hecho && (
        <div className="maqueta-hecha">
          <div className="rejilla">
            <div className="cifra"><b>{mm(hecho.medidas.ancho_mm)} × {mm(hecho.medidas.alto_mm)}</b><span>mm, con sangrado</span></div>
            <div className="cifra"><b>{mm(hecho.medidas.lomo_mm)} mm</b><span>lomo · {hecho.paginas} págs</span></div>
            <div className="cifra"><b>{hecho.portada_ppp ?? "—"}</b><span>ppp de la portada</span></div>
          </div>
          {prueba && <img className="cubierta-prueba" src={prueba} alt="La prueba de la cubierta con las guías de KDP" />}
          <p className="vacio">
            En la prueba: <b style={{ color: "#ff2828" }}>rojo</b>, la línea de corte; <b style={{ color: "#285aff" }}>azul</b>, los pliegues del lomo;
            <b style={{ color: "#00c850" }}> verde</b>, la zona segura (todo el texto dentro); <b style={{ color: "#e6b400" }}>amarillo</b>, el código de barras, que pone Amazon.
          </p>
          {hecho.portada_ppp != null && hecho.portada_ppp < 300 && (
            <div className="aviso">La cubierta tiene las medidas exactas, pero la portada imprime a {hecho.portada_ppp} ppp: en papel se verá blanda. Súbela a más resolución y vuelve a montarla.</div>
          )}
          <ul className="maqueta-ficheros">
            <li><b>La cubierta para KDP (PDF)</b><span className="mono">{nombreDeRuta(hecho.pdf)}</span> <button className="btn texto" onClick={() => mostrarEnCarpeta(hecho.pdf)}>Enseñar en la carpeta</button></li>
          </ul>
          <p className="vacio">
            En KDP: <b>Contenido de tapa blanda → Cubierta → «Cargar un archivo de cubierta que ya tengas»</b> y sube ese PDF. Si
            cambias el número de páginas (vuelves a maquetar), vuelve a montar la cubierta: el lomo cambia con ellas.
          </p>
        </div>
      )}
    </section>
  );
}
